/* Shared voyage-name composition and numbering-year resolution, used by both
 * the backend regenerator and the frontend commit path.
 *
 * The lazy counter in voyage_prefix_sequences is keyed by the prefix string
 * (`v{shipId}_{prefix}`), so both paths must produce the same prefix — any
 * divergence splits the key and silently restarts or collides numbering.
 *
 * Time math is UTC throughout: a voyage's numbering year must not depend on
 * the viewer's timezone. `loadingEnd` is a UTC instant (epoch ms). */

export type YearFormat = 'yy' | 'yyyy' | 'none';

/** Normalize either the lowercase Postgres enum value (`'yyyy'`) or the
 * uppercase GraphQL enum value (`VoyageNamingYearFormat.YYYY === 'YYYY'`) to
 * the canonical lowercase form. */
export const normalizeYearFormat = (value: string): YearFormat => {
  switch (value.toLowerCase()) {
    case 'yy':
      return 'yy';
    case 'none':
      return 'none';
    default:
      return 'yyyy';
  }
};

export type VoyageNameRule = {
  prefix: string | null;
  separator: string;
  year: YearFormat;
  zerosPad: number;
  // nullable: the "include this vessel part" flags come from a GraphQL rule
  // where they are optional. Absent is treated as off (falsy in the composer).
  vesselFullname: boolean | null | undefined;
  vesselShortname: boolean | null | undefined;
  vesselImo: boolean | null | undefined;
};

export type VoyageNameVessel = {
  fullName: string | null;
  shortName: string | null;
  // string | number so backend (numeric imo), frontend (branded Int) and the
  // admin placeholder ('12345678') all pass without a coercion adapter.
  imo: string | number | null;
};

/** Per-vessel override of when a numbering year begins. `resetsOn` is the
 * instant (epoch ms) the named `year` starts for that vessel. */
export type VoyageNumberingReset = { year: number; resetsOn: number };

export const buildResetMap = (
  resets: readonly VoyageNumberingReset[],
): Map<number, number> => new Map(resets.map((r) => [r.year, r.resetsOn]));

/** Numbering year `year`'s start: per-vessel override, else Jan 1 UTC. */
export const effYearStart = (
  year: number,
  resetsByYear: ReadonlyMap<number, number>,
): number => resetsByYear.get(year) ?? Date.UTC(year, 0, 1);

/** Numbering year for a loading: the year Y whose effective window
 * [effYearStart(Y), effYearStart(Y+1)) contains `loadingEnd`. A per-vessel
 * reset moves a voyage at most ~1 year from its calendar year, so checking the
 * neighbouring years is sufficient. */
export const resolveYear = (
  loadingEnd: number,
  resetsByYear: ReadonlyMap<number, number>,
): number => {
  const calendar = new Date(loadingEnd).getUTCFullYear();
  for (const y of [calendar + 1, calendar, calendar - 1]) {
    if (
      loadingEnd >= effYearStart(y, resetsByYear) &&
      loadingEnd < effYearStart(y + 1, resetsByYear)
    ) {
      return y;
    }
  }
  return calendar;
};

export const formatYearSegment = (format: YearFormat, year: number): string => {
  switch (format) {
    case 'none':
      return '';
    case 'yy':
      return String(year).slice(-2);
    case 'yyyy':
      return String(year);
  }
};

/** Name without the trailing counter. This IS the voyage_prefix_sequences key
 * body (after the `v{shipId}_` scope), so it must be identical on every path. */
export const formatVoyagePrefix = (
  rule: VoyageNameRule,
  vessel: VoyageNameVessel,
  year: number,
): string => {
  const sep = rule.separator;
  const parts: string[] = [];
  if (rule.prefix) parts.push(rule.prefix);
  const vesselInfo = [
    rule.vesselFullname ? vessel.fullName : null,
    rule.vesselShortname ? vessel.shortName : null,
    rule.vesselImo && vessel.imo != null ? String(vessel.imo) : null,
  ]
    .filter((p): p is string => !!p)
    .join(sep);
  if (vesselInfo !== '') parts.push(vesselInfo);
  const yearSeg = formatYearSegment(rule.year, year);
  if (yearSeg !== '') parts.push(yearSeg);
  return parts.join(sep);
};

/** Full voyage name: prefix + separator + zero-padded counter. Mirrors the SQL
 * allocator's assembly in voyage_prefix_get_next_ids. */
export const formatVoyageName = (
  rule: VoyageNameRule,
  vessel: VoyageNameVessel,
  year: number,
  counter: number,
): string => {
  const prefix = formatVoyagePrefix(rule, vessel, year);
  const counterStr = String(counter).padStart(rule.zerosPad, '0');
  return prefix === '' ? counterStr : [prefix, counterStr].join(rule.separator);
};
